import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import debounce from "lodash.debounce";

const AutocompleteWithPagination = ({
  selectedItem,
  onDataChange,
  selectedClient,
  width = 250,
  errorMessage,
  hideLabel,
  optionKey,
  apiEndPoint,
  disabled = false,
  customLabel,
  backgroundColor,
  data = [],
}) => {
  const [allData, setAllData] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const clientId = localStorage.getItem("client_id");
  const client_ref = useRef();
  const searchValueRef = useRef("");
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (data && data.length > 0) {
      const enriched = [...data];
      if (data.length >= 15) {
        enriched.push({ [optionKey]: "Load More", isLoadMore: true });
      }
      setAllData(enriched);
    }
  }, [data, apiEndPoint]);

  let tim;

  const clear_data = () => {
    setAllData([]);
    setInputValue("");
    setPage(0);
    setLoading(false);
    setLoadMoreLoading(false);
    client_ref.current = null;
    searchValueRef.current = "";
    autocompleteRef.current = null;
    tim && clearTimeout(tim);
    setLoadMoreLoading(false);
    console.log("Cleared autocomplete data");
  };

  useEffect(() => {
    client_ref.current = selectedClient?._id || clientId;
  }, [clientId, selectedClient]);

  useEffect(() => {
    if (selectedItem && selectedItem[optionKey]) {
      setInputValue(selectedItem[optionKey]);
      searchValueRef.current = selectedItem[optionKey];
    }
  }, [selectedItem, optionKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target)
      ) {
        tim = setTimeout(() => {
          setInputValue(searchValueRef.current);
        }, 100);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clear_data();
    };
  }, []);

  const fetchData = useCallback(
    debounce(async (searchQuery, pageNumber, isLoadMore = false) => {
      if (isLoadMore) setLoadMoreLoading(true);
      else setLoading(true);

      try {
        const limit = 15;
        const response = await apiEndPoint(
          limit,
          pageNumber * limit,
          searchQuery || undefined,
          client_ref.current
        );

        let data = [...(response?.data || [])];

        if (pageNumber === 0) setPage(0);

        if (data.length === 0) {
          setAllData([{ [optionKey]: "No data found", isNoData: true }]);
          setPage(-1);
        } else {
          if (data.length === limit) {
            data.push({ [optionKey]: "Load More", isLoadMore: true });
          }
          setAllData((prevData) => [
            ...(pageNumber === 0 ? [] : prevData.slice(0, -1)),
            ...data,
          ]);
          setPage(data.length < limit ? -1 : pageNumber);
        }
      } catch (error) {
        console.error("Fetch failed:", error);
      } finally {
        setLoading(false);
        setLoadMoreLoading(false);
      }
    }, 300),
    []
  );

  const handleDataChange = (event, value) => {
    if (value?.isLoadMore) return;
    onDataChange?.(value);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    searchValueRef.current = value;
    fetchData(value, 0);
    setPage(0);
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason !== "blur") {
      setInputValue(newInputValue);
      searchValueRef.current = newInputValue;
    } else {
      setTimeout(() => setInputValue(searchValueRef.current), 0);
    }
  };

  const handleLoadMoreClick = () => {
    if (page !== -1 && !loading && !loadMoreLoading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchData(inputValue, nextPage, true);
    }
  };

  const handleTextFieldClick = () => {
    const hasRealData = allData.some(
      (item) => !item.isNoData && !item.isLoadMore
    );
    if (!loading) {
      fetchData("", 0);
    }
  };

  return (
    <div ref={autocompleteRef}>
      <Autocomplete
        disableOpenOnFocus={false}
        forcePopupIcon
        disabled={disabled}
        size="small"
        id="data-select"
        options={allData}
        getOptionLabel={(option) => {
          if (!option) return "";
          if (option.isLoadMore) return "Load More";
          if (option.isNoData) return "No data found";
          if (typeof optionKey === "function") return optionKey(option);
          return option[optionKey] || "";
        }}
        value={selectedItem || null}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onFocusCapture={handleTextFieldClick}
        onChange={handleDataChange}
        filterOptions={(options) => options}
        clearOnEscape
        isOptionEqualToValue={(option, value) => {
          if (option?.isLoadMore || value?.isLoadMore) return false;
          const optionLabel =
            typeof optionKey === "function"
              ? optionKey(option)
              : option[optionKey];
          const valueLabel =
            typeof optionKey === "function"
              ? optionKey(value)
              : value?.[optionKey];
          return optionLabel === valueLabel;
        }}
        freeSolo
        renderOption={(props, option) => {
          const label =
            typeof optionKey === "function"
              ? optionKey(option)
              : option[optionKey] || "";

          if (option.isLoadMore) {
            return (
              <li
                {...props}
                onClick={handleLoadMoreClick}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #ddd",
                  padding: "10px",
                }}
              >
                <Typography
                  color="primary"
                  variant="body2"
                  style={{ marginRight: 8 }}
                >
                  Load More...
                </Typography>
                {loadMoreLoading && (
                  <CircularProgress color="inherit" size={16} />
                )}
              </li>
            );
          }

          if (option.isNoData) {
            return (
              <li
                {...props}
                style={{
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #ddd",
                  padding: "10px",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No data found
                </Typography>
              </li>
            );
          }

          return (
            <li
              {...props}
              title={label}
              style={{
                backgroundColor: "#f5f5f5",
                borderBottom: "1px solid #ddd",
                padding: "10px",
              }}
            >
              <p
                style={{
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  margin: 0,
                  width: "100%",
                }}
              >
                {label}
              </p>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            value={inputValue}
            style={{ backgroundColor, borderRadius: 10 }}
            margin="dense"
            label={
              hideLabel ? "" : `Select ${customLabel || optionKey || "data"}`
            }
            variant="outlined"
            onChange={handleSearchChange}
            onBlur={() =>
              setTimeout(() => setInputValue(searchValueRef.current), 0)
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              onClick: handleTextFieldClick,
            }}
            error={!!errorMessage}
            helperText={errorMessage}
            onClick={handleTextFieldClick}
          />
        )}
        style={{ width }}
      />
    </div>
  );
};

export default AutocompleteWithPagination;
